import { Injectable, NgZone, OnDestroy } from '@angular/core';
import * as mapboxgl from 'mapbox-gl';
import {
  Observable,
  BehaviorSubject,
  from,
  Subject,
  of,
  throwError,
  EMPTY,
  Subscription
} from 'rxjs';
import {
  timeout,
  retry,
  catchError,
  distinctUntilChanged,
  finalize,
  tap,
  shareReplay
} from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { SkyboxService } from './skybox.service';
import { Coordinates, MapConfiguration } from '../../interfaces/master-interfaces';


/**
 * MapState tracks the various states the map can be in
 */
export enum MapState {
  IDLE = 'idle',
  LOADING = 'loading',
  LOADED = 'loaded',
  ERROR = 'error'
}



@Injectable({
  providedIn: 'root'
})

export class MapService implements OnDestroy {


  private readonly GRATICULE_CONFIG = {
    latitudeInterval: 10,
    longitudeInterval: 10,
    majorInterval: 30,
    minorColor: '#7a8ba6',
    majorColor: '#9fb3cc',
    equatorColor: '#ffb547',
    meridianColor: '#4fd1c5',
    labelColor: '#cfe0f5',
    minorOpacity: 0.18,
    majorOpacity: 0.35,
    axisOpacity: 0.9,
    glowOpacity: 0.22,
    widthScale: 1,
    showLabels: true
  };

  /** Live config — what's actually on the map right now. */
  private graticuleConfig = { ...this.GRATICULE_CONFIG };

  private readonly GRATICULE_LAYER_IDS = [
    'graticule-axis-glow',
    'graticule-minor',
    'graticule-major',
    'graticule-axis',
    'graticule-labels'
  ];

  private graticuleLayersAdded = false;



  // Map instance and state
  map: mapboxgl.Map | null = null;
  private resizeObserver: ResizeObserver | null = null;
  private mapState = new BehaviorSubject<MapState>(MapState.IDLE);
  public mapInitialized = new BehaviorSubject<boolean>(false);
  mapFullyLoadedSubject = new BehaviorSubject<boolean>(false);
  private destroy$ = new Subject<void>();
  private mapObserverSubscription: Subscription | null = null;
  private pendingNavigation: [number, number] | null = null;
  private layers: Set<string> = new Set();
  public initialCoordinates: Coordinates | null = null;
  public initialZoom: number | null = null;

  // Configuration
  private readonly mapConfig: MapConfiguration = {
    style: environment.mapbox.styleUrl,
    center: [0, 0], // Will be set dynamically
    zoom: 1,
    accessToken: environment.mapbox.accessToken,
    preserveDrawingBuffer: false,
    antialias: false
  };

  // Public observables
  readonly mapState$ = this.mapState.asObservable().pipe(distinctUntilChanged());
  readonly mapInitialized$ = this.mapInitialized.asObservable().pipe(distinctUntilChanged());
  readonly mapFullyLoaded$ = this.mapFullyLoadedSubject.asObservable().pipe(
    distinctUntilChanged(),
    shareReplay(1)
  );

  constructor(
    private _ngZone: NgZone,
    private _skyboxService: SkyboxService
  ) {
    this.tryExtractCoordinatesFromUrl();
  }

  preloadMapAssets(): Observable<boolean> {
    if (this.mapState.value !== MapState.IDLE) {
      return of(true);
    }

    return of(true).pipe(
      tap(() => {
        // Here you can preload custom markers, icons, or other map resources
        this.mapState.next(MapState.LOADING);
      }),
      catchError(error => {
        console.error('[MapService] Preload error:', error);
        this.mapState.next(MapState.ERROR);
        return throwError(() => new Error('Map preload failed'));
      })
    );
  }

  initializeMapPostLogin(): Observable<boolean> {
    if (this.map) {
      this.destroyMap();
    };
    this.mapState.next(MapState.LOADING);
    // console.log('[MapService] Starting map initialization');
    // console.log('[MapService] Current URL:', window.location.href);
    // console.log('[MapService] Looking for map container...');

    return new Observable<boolean>(observer => {
      // Try finding the container a few times before setting up the observer
      let retries = 0;
      const maxRetries = 5;
      const retryInterval = 300; // ms

      const attemptFindContainer = () => {
        const container = document.getElementById('map');
        //console.log(`[MapService] Container search attempt ${retries + 1}:`, !!container);

        if (container) {
          this.initializeMapInstance(container)
            .then(() => {
              observer.next(true);
              observer.complete();
            })
            .catch(error => {
              observer.error(error);
            });
          return true;
        }

        retries++;
        if (retries < maxRetries) {
          setTimeout(attemptFindContainer, retryInterval);
          return false;
        }

        // After max retries, set up the mutation observer
        setupMutationObserver();
        return false;
      };

      const setupMutationObserver = () => {
        //console.log('[MapService] Setting up mutation observer');
        const containerObserver = new MutationObserver((mutations, obs) => {
          const container = document.getElementById('map');
          if (container) {
            //console.log('[MapService] Container found by mutation observer');
            obs.disconnect();
            this.initializeMapInstance(container)
              .then(() => {
                observer.next(true);
                observer.complete();
              })
              .catch(error => {
                observer.error(error);
              });
            this.mapObserverSubscription = null;
          }
        });

        containerObserver.observe(document.body, {
          childList: true,
          subtree: true
        });

        // Set timeout to avoid endless watching
        const timeout = setTimeout(() => {
          containerObserver.disconnect();
          observer.error(new Error('Map container not found after timeout'));
        }, 5000);

        this.mapObserverSubscription = new Subscription(() => {
          clearTimeout(timeout);
          containerObserver.disconnect();
        });
      };

      // Start the container search process
      attemptFindContainer();

      // Cleanup function for when the observable is unsubscribed
      return () => {
        if (this.mapObserverSubscription) {
          this.mapObserverSubscription.unsubscribe();
          this.mapObserverSubscription = null;
        }
      };
    }).pipe(
      timeout(5000),
      retry(1),
      catchError(error => {
        this.mapState.next(MapState.ERROR);
        console.error('[MapService] Map initialization failed:', error);
        return of(false);
      }),
      finalize(() => {
        if (this.mapObserverSubscription) {
          this.mapObserverSubscription.unsubscribe();
          this.mapObserverSubscription = null;
        }
      })
    );
  }

  getMap(): mapboxgl.Map {
    if (!this.map || !this.mapInitialized.value) {
      throw new Error('Map is not initialized');
    }
    return this.map;
  }

  safeRemoveSource(sourceId: string): void {
    if (!this.map) return;

    const style = this.map.getStyle();
    if (!style || !style.layers) {
      console.warn(`[MapService] Style not ready, cannot remove source: ${sourceId}`);
      return;
    }

    // Step 1: Find and remove ALL layers using this source
    const layersToRemove = style.layers
      .filter(layer => (layer as any).source === sourceId)
      .map(layer => layer.id);

    layersToRemove.forEach(layerId => {
      try {
        this.map!.removeLayer(layerId);
      } catch (e) {
        console.warn(`[MapService] Layer already removed: ${layerId}`, e);
      }
    });

    // Step 2: NOW remove the source
    try {
      this.map.removeSource(sourceId);
    } catch (e) {
      console.warn(`[MapService] Source already removed: ${sourceId}`, e);
    }
  }

  private async initializeMapInstance(container: HTMLElement): Promise<void> {
    try {
      let coordinates;
      let zoom = this.mapConfig.zoom;

      // Check for coordinates - should always be set by tryExtractCoordinatesFromUrl
      if (this.initialCoordinates) {
        coordinates = this.initialCoordinates;
        zoom = this.initialZoom || zoom;
        // console.log('[MapService] Using initial coordinates:', coordinates);
      } else {
        // Fallback - should rarely happen since tryExtractCoordinatesFromUrl sets initialCoordinates
        console.warn('[MapService] No initial coordinates found, using default');
        coordinates = this.getDefaultCoordinates();
      }

      // Validate coordinates before using them
      if (!coordinates ||
        coordinates.longitude === undefined ||
        coordinates.latitude === undefined ||
        isNaN(coordinates.longitude) ||
        isNaN(coordinates.latitude)) {
        console.error('[MapService] Invalid coordinates, using defaults');
        coordinates = this.getDefaultCoordinates();
      }

      //console.log('[MapService] Creating map with coordinates:', coordinates);

      this.map = new mapboxgl.Map({
        container,
        style: this.mapConfig.style,
        center: [coordinates.longitude, coordinates.latitude],
        zoom: zoom,
        projection: 'globe',
        accessToken: this.mapConfig.accessToken,
        preserveDrawingBuffer: this.mapConfig.preserveDrawingBuffer,
        antialias: true,
      });

      (window as any).mbmap = this.map;

      this.resizeObserver = new ResizeObserver(() => this.map?.resize());
      this.resizeObserver.observe(container);

      return new Promise<void>((resolve, reject) => {
        if (!this.map) {
          reject(new Error('Map creation failed'));
          return;
        }

        this.map.once('load', () => {
          this._ngZone.run(() => {
            this.mapState.next(MapState.LOADED);
            this.mapInitialized.next(true);
            this.setupMapEventListeners();
            this.resizeMap();

            // Set up terrain so beings render on the globe correctly
            this.setupTerrain();
            this.addGraticule();
            this._skyboxService.attach(this.map!);
            resolve();
          });
        });

        this.map.once('error', (e: any) => {
          this._ngZone.run(() => {
            console.error('[MapService] ✗ Map error:', e?.error?.message ?? e?.error ?? e);
            console.error('[MapService] source/layer:', e?.sourceId, e?.source?.type);
            this.mapState.next(MapState.ERROR);
            this.mapInitialized.next(false);
            reject(e?.error ?? new Error('Map failed to load'));
          });
        });
      });
    } catch (error) {
      console.error('[MapService] Error initializing map instance:', error);
      this.mapState.next(MapState.ERROR);
      this.mapInitialized.next(false);
      throw error;
    }
  }

  private setupTerrain(): void {
    if (!this.map) return;
    try {
      if (!this.map.getSource('mapbox-dem')) {
        this.map.addSource('mapbox-dem', {
          type: 'raster-dem',
          url: 'mapbox://mapbox.mapbox-terrain-dem-v1',
          tileSize: 512,
          maxzoom: 14
        });
      }
      this.map.setTerrain({ source: 'mapbox-dem', exaggeration: 1.5 });
    } catch (error) {
      console.warn('[MapService] Error setting up terrain:', error);
    }
  }

  private setupStyleLayers(): void {
    this.setupTerrain();
    this.addGraticule({
      latitudeInterval: 10,
      longitudeInterval: 10,
      majorInterval: 30,
      minorOpacity: 0.12,   // was the single lineOpacity
      majorOpacity: 0.3,
      axisOpacity: 0.85
    });
    this.addNightDayGradientOverlay();
    console.log('[MapService] ✓ Style layers ready');
  }

  getMapIfReady(): mapboxgl.Map | null {
    return this.map && this.mapInitialized.value ? this.map : null;
  }

  public addNightDayGradientOverlay(): void {
    if (!this.map) return;

    try {
      // Remove existing overlay if any
      this.removeNightDayGradientOverlay();

      // Create overlay div with radial gradient
      const overlay = document.createElement('div');
      overlay.id = 'night-day-overlay';
      overlay.style.position = 'absolute';
      overlay.style.top = '0';
      overlay.style.left = '0';
      overlay.style.width = '100%';
      overlay.style.height = '100%';
      overlay.style.pointerEvents = 'none';
      overlay.style.zIndex = '1';

      // Radial gradient: bright center, dark edges
      overlay.style.background = `
  radial-gradient(
    circle at center,
    rgba(255, 150, 0, 0) 0%,
    rgba(100, 80, 180, 0.2) 25%,
    rgba(50, 50, 100, 0.5) 50%,
    rgba(0, 0, 0, 0.8) 100%
  )
`;

      // Find map container and insert overlay
      const mapContainer = document.querySelector('.map-container');
      if (mapContainer) {
        const parent = mapContainer.parentElement;
        if (parent) {
          parent.style.position = 'relative';  // Ensure parent is positioned
          parent.appendChild(overlay);
          console.log('[MapService] ✓ Night/day gradient overlay added');
        }
      }
    } catch (error) {
      console.error('[MapService] Error adding gradient overlay:', error);
    }
  }

  public removeNightDayGradientOverlay(): void {
    const overlay = document.getElementById('night-day-overlay');
    if (overlay) {
      overlay.remove();
    }
  }


  private setupMapEventListeners(): void {
    if (!this.map) return;

    // Listen for style changes
    this.map.on('style.load', () => {
      console.log('[MapService] ✓ style.load fired!');
      this._ngZone.run(() => {
        this.mapFullyLoadedSubject.next(true);
        this.graticuleLayersAdded = false;   // layers were wiped with the style
        this.setupStyleLayers();
      });
    });

    // Listen for render events
    this.map.on('render', () => {
      if (this.map && !this.map.isMoving()) {
        this._ngZone.run(() => this.resizeMap());
      }
    });

    // Debug: log when map is ready
    this.map.on('load', () => {
      console.log('[MapService] ✓ map load fired!');
    });
  }


  resizeMap(): void {
    if (this.map) {
      this.map.resize();
    }
  }

  getDefaultCoordinates(): { longitude: number, latitude: number } {
    return {
      //Geneva
      longitude: 6.1373996,
      latitude: 46.2129931
    };
  }

  private generateRandomCoordinates(): Coordinates {
    // Use more constrained values to ensure we get reasonable coordinates
    // These bounds cover most populated areas and avoid extreme polar regions
    const minLatitude = -60;  // Avoid Antarctic
    const maxLatitude = 65;   // Avoid far northern regions
    const minLongitude = -170;
    const maxLongitude = 170;
    const latitude = Math.random() * (maxLatitude - minLatitude) + minLatitude;
    const longitude = Math.random() * (maxLongitude - minLongitude) + minLongitude;

    // console.log('[MapService] Generated random coordinates:', { longitude, latitude });

    // Validate coordinates
    if (isNaN(latitude) || isNaN(longitude)) {
      console.error('[MapService] Invalid random coordinates, using defaults');
      return this.getDefaultCoordinates();
    }

    return { longitude, latitude };
  }


  tryExtractCoordinatesFromUrl(): void {
    try {
      const hash = window.location.hash;
      // console.log('[MapService] Checking URL for coordinates:', hash);

      // Check if we're on the location route
      if (hash.includes('#/location')) {
        const paramString = hash.split('?')[1] || '';
        const params = new URLSearchParams(paramString);
        const lat = params.get('lat');
        const lng = params.get('lng');

        if (lat && lng) {
          const latitude = parseFloat(lat);
          const longitude = parseFloat(lng);

          if (!isNaN(longitude) && !isNaN(latitude)) {
            // console.log('[MapService] Found coordinates in URL:', longitude, latitude);
            this.initialCoordinates = {
              longitude: longitude,
              latitude: latitude,
            };
            this.initialZoom = 5; // Default zoom for location view
            return;
          }
        }
      }

      // console.log('[MapService] No valid coordinates found in URL, using random');

      // Set random coordinates since none were found in URL
      this.initialCoordinates = this.generateRandomCoordinates();

      // console.log('[MapService] Set random coordinates:', this.initialCoordinates);

    } catch (error) {
      console.warn('[MapService] Error extracting coordinates from URL:', error);

      // In case of error, still set random coordinates
      this.initialCoordinates = this.generateRandomCoordinates();

      // console.log('[MapService] Set random coordinates after error:', this.initialCoordinates);
    }
  }


  destroyMap(): void {
    // console.log('[MapService] ⚠️ destroyMap() called');
    console.trace('[MapService] Stack trace:');  // Shows where destroyMap was called from

    if (this.map) {
      this.removeGraticule();
      this._skyboxService.detach();
      this.map.remove();
      this.map = null;
      this.mapInitialized.next(false);
      this.mapFullyLoadedSubject.next(false);
      this.layers.clear();
    }
  }

  private generateGraticuleGeoJSON(
    latInterval: number,
    lonInterval: number,
    majorInterval = 30
  ): GeoJSON.FeatureCollection {
    const features: GeoJSON.Feature[] = [];
    const STEP = 2; // densify so lines curve correctly under globe projection

    // Parallels
    for (let lat = -80; lat <= 80; lat += latInterval) {
      const coords: [number, number][] = [];
      for (let lon = -180; lon <= 180; lon += STEP) coords.push([lon, lat]);

      features.push({
        type: 'Feature',
        geometry: { type: 'LineString', coordinates: coords },
        properties: {
          kind: 'parallel',
          deg: lat,
          absDeg: Math.abs(lat),
          cls: lat === 0 ? 'equator' : (lat % majorInterval === 0 ? 'major' : 'minor'),
          // label: lat === 0 ? 'EQUATOR' : `${Math.abs(lat)}°${lat > 0 ? 'N' : 'S'}`
        }
      });
    }

    // Meridians
    for (let lon = -180; lon < 180; lon += lonInterval) {
      const coords: [number, number][] = [];
      for (let lat = -85; lat <= 85; lat += STEP) coords.push([lon, lat]);

      features.push({
        type: 'Feature',
        geometry: { type: 'LineString', coordinates: coords },
        properties: {
          kind: 'meridian',
          deg: lon,
          absDeg: 0,
          cls: lon === 0 ? 'prime' : (lon % majorInterval === 0 ? 'major' : 'minor'),
          // label: lon === 0 ? 'GREENWICH' : `${Math.abs(lon)}°${lon > 0 ? 'E' : 'W'}`
        }
      });
    }

    return { type: 'FeatureCollection', features };
  }


  public addGraticule(config?: Partial<typeof this.GRATICULE_CONFIG>): void {
    if (!this.map || !this.mapInitialized.value) {
      console.warn('[MapService] Map not initialized, cannot add graticule');
      return;
    }
    if (this.graticuleLayersAdded) {
      console.warn('[MapService] Graticule already added');
      return;
    }

    const cfg = { ...this.GRATICULE_CONFIG, ...config };

    // Shared: fade parallels out toward the poles where they bunch up
    const polarFade: any = [
      'interpolate', ['linear'], ['get', 'absDeg'],
      0, 1, 55, 1, 80, 0.25
    ];

    const axisColor: any = [
      'match', ['get', 'cls'],
      'equator', cfg.equatorColor,
      'prime', cfg.meridianColor,
      'transparent'
    ];

    try {
      this.map.addSource('graticule-source', {
        type: 'geojson',
        data: this.generateGraticuleGeoJSON(
          cfg.latitudeInterval,
          cfg.longitudeInterval,
          cfg.majorInterval
        )
      });

      // --- 1. Soft glow beneath the two hero lines ---
      this.map.addLayer({
        id: 'graticule-axis-glow',
        type: 'line',
        source: 'graticule-source',
        filter: ['in', ['get', 'cls'], ['literal', ['equator', 'prime']]],
        paint: {
          'line-color': axisColor,
          'line-width': ['interpolate', ['linear'], ['zoom'], 0, 6, 4, 10, 8, 16],
          'line-blur': ['interpolate', ['linear'], ['zoom'], 0, 4, 8, 12],
          'line-opacity': 0.22
        }
      });

      // --- 2. Minor grid: hairline, dashed, recessive ---
      this.map.addLayer({
        id: 'graticule-minor',
        type: 'line',
        source: 'graticule-source',
        filter: ['==', ['get', 'cls'], 'minor'],
        paint: {
          'line-color': cfg.minorColor,
          'line-width': ['interpolate', ['linear'], ['zoom'], 0, 0.3, 5, 0.5, 10, 0.8],
          'line-dasharray': [2, 4],
          'line-opacity': ['*', 0.18, polarFade]
        }
      });

      // --- 3. Major grid every 30°: solid, slightly brighter ---
      this.map.addLayer({
        id: 'graticule-major',
        type: 'line',
        source: 'graticule-source',
        filter: ['==', ['get', 'cls'], 'major'],
        paint: {
          'line-color': cfg.majorColor,
          'line-width': ['interpolate', ['linear'], ['zoom'], 0, 0.4, 5, 0.5, 10, 0.7],
          'line-opacity': ['*', 0.16, polarFade]
        }
      });

      // --- 4. Equator + Greenwich: crisp, saturated, always on top ---
      this.map.addLayer({
        id: 'graticule-axis',
        type: 'line',
        source: 'graticule-source',
        filter: ['in', ['get', 'cls'], ['literal', ['equator', 'prime']]],
        paint: {
          'line-color': axisColor,
          'line-width': ['interpolate', ['linear'], ['zoom'], 0, 1.1, 4, 1.6, 10, 2.2],
          'line-opacity': 0.9
        }
      });

      // --- 5. Labels riding along the lines ---
      // if (cfg.showLabels) {
      //   this.map.addLayer({
      //     id: 'graticule-labels',
      //     type: 'symbol',
      //     source: 'graticule-source',
      //     filter: ['in', ['get', 'cls'], ['literal', ['equator', 'prime', 'major']]],
      //     layout: {
      //       'symbol-placement': 'line',
      //       'symbol-spacing': 400,
      //       'text-field': ['get', 'label'],
      //       'text-font': ['Open Sans Regular'],
      //       'text-size': ['interpolate', ['linear'], ['zoom'], 0, 9, 6, 12],
      //       'text-letter-spacing': 0.22,
      //       'text-max-angle': 90,
      //       'text-keep-upright': true,
      //       'text-allow-overlap': false
      //     },
      //     paint: {
      //       'text-color': [
      //         'match', ['get', 'cls'],
      //         'equator', cfg.equatorColor,
      //         'prime', cfg.meridianColor,
      //         cfg.labelColor
      //       ],
      //       'text-opacity': ['match', ['get', 'cls'], 'major', 0.45, 0.85],
      //       'text-halo-color': 'rgba(0,0,0,0.6)',
      //       'text-halo-width': 1.2
      //     }
      //   });
      // }

      this.graticuleLayersAdded = true;
    } catch (error) {
      console.error('[MapService] Error adding graticule:', error);
    }
  }


  public removeGraticule(): void {
    if (!this.map) return;

    try {
      if (this.map.getLayer('graticule-lines')) {
        this.map.removeLayer('graticule-lines');
      }
      if (this.map.getSource('graticule-source')) {
        this.map.removeSource('graticule-source');
      }
      this.graticuleLayersAdded = false;
      // console.log('[MapService] Graticule removed from map');
    } catch (error) {
      console.error('[MapService] Error removing graticule:', error);
    }
  }




  public toggleGraticule(): void {
    if (!this.map) return;

    try {
      const layer = this.map.getLayer('graticule-lines');
      if (layer) {
        const visibility = this.map.getLayoutProperty('graticule-lines', 'visibility');
        const newVisibility = visibility === 'visible' ? 'none' : 'visible';
        this.map.setLayoutProperty('graticule-lines', 'visibility', newVisibility);
        //  console.log(`[MapService] Graticule visibility: ${newVisibility}`);
      }
    } catch (error) {
      console.error('[MapService] Error toggling graticule:', error);
    }
  }

  private buildGraticulePaint(cfg: typeof this.GRATICULE_CONFIG) {
    const w = (a: number, b: number, c: number): any =>
      ['interpolate', ['linear'], ['zoom'],
        0, a * cfg.widthScale, 5, b * cfg.widthScale, 10, c * cfg.widthScale];

    const polarFade: any = [
      'interpolate', ['linear'], ['get', 'absDeg'],
      0, 1, 55, 1, 80, 0.25
    ];

    const axisColor: any = [
      'match', ['get', 'cls'],
      'equator', cfg.equatorColor,
      'prime', cfg.meridianColor,
      'transparent'
    ];

    return {
      glow: {
        'line-color': axisColor,
        'line-width': w(6, 12, 16),
        'line-blur': ['interpolate', ['linear'], ['zoom'], 0, 4, 8, 12] as any,
        'line-opacity': cfg.glowOpacity
      },
      minor: {
        'line-color': cfg.minorColor,
        'line-width': w(0.3, 0.5, 0.8),
        'line-dasharray': [2, 4] as any,
        'line-opacity': ['*', cfg.minorOpacity, polarFade] as any
      },
      major: {
        'line-color': cfg.majorColor,
        'line-width': w(0.5, 0.8, 1.2),
        'line-opacity': ['*', cfg.majorOpacity, polarFade] as any
      },
      axis: {
        'line-color': axisColor,
        'line-width': w(1.1, 1.6, 2.2),
        'line-opacity': cfg.axisOpacity
      },
      labelText: [
        'match', ['get', 'cls'],
        'equator', cfg.equatorColor,
        'prime', cfg.meridianColor,
        cfg.labelColor
      ] as any
    };
  }


  public updateGraticuleStyle(
    config: Partial<typeof this.GRATICULE_CONFIG>
  ): void {
    if (!this.map || !this.graticuleLayersAdded) return;

    const cfg = { ...this.graticuleConfig, ...config };

    // Interval changes are structural, not paint — regenerate the source.
    const geometryChanged =
      cfg.latitudeInterval !== this.graticuleConfig.latitudeInterval ||
      cfg.longitudeInterval !== this.graticuleConfig.longitudeInterval ||
      cfg.majorInterval !== this.graticuleConfig.majorInterval;

    try {
      if (geometryChanged) {
        const src = this.map.getSource('graticule-source') as mapboxgl.GeoJSONSource;
        src?.setData(this.generateGraticuleGeoJSON(
          cfg.latitudeInterval,
          cfg.longitudeInterval,
          cfg.majorInterval
        ));
      }

      const paint = this.buildGraticulePaint(cfg);

      type PaintPropName = Parameters<mapboxgl.Map['setPaintProperty']>[1];

      const apply = (layerId: string, props: Record<string, unknown>) => {
        if (!this.map!.getLayer(layerId)) return;
        (Object.entries(props) as [PaintPropName, unknown][]).forEach(([prop, value]) =>
          this.map!.setPaintProperty(layerId, prop, value as any)
        );
      };

      apply('graticule-axis-glow', paint.glow);
      apply('graticule-minor', paint.minor);
      apply('graticule-major', paint.major);
      apply('graticule-axis', paint.axis);

      if (this.map.getLayer('graticule-labels')) {
        this.map.setPaintProperty('graticule-labels', 'text-color', paint.labelText);
        if (config.showLabels !== undefined) {
          this.map.setLayoutProperty(
            'graticule-labels',
            'visibility',
            cfg.showLabels ? 'visible' : 'none'
          );
        }
      }

      this.graticuleConfig = cfg;
    } catch (error) {
      console.error('[MapService] Error updating graticule style:', error);
    }
  }

  public setGraticuleTheme(theme: 'nautical' | 'blueprint' | 'minimal'): void {
    const themes = {
      nautical: { equatorColor: '#ffb547', meridianColor: '#4fd1c5', minorColor: '#7a8ba6' },
      blueprint: {
        equatorColor: '#ffffff', meridianColor: '#ffffff', minorColor: '#5b8dd9',
        majorColor: '#8ab4f8', axisOpacity: 0.7, glowOpacity: 0.1
      },
      minimal: {
        equatorColor: '#e8e8e8', meridianColor: '#e8e8e8', minorOpacity: 0.08,
        majorOpacity: 0.15, glowOpacity: 0, showLabels: false
      }
    };
    this.updateGraticuleStyle(themes[theme]);
  }


  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.destroyMap();

    if (this.mapObserverSubscription) {
      this.mapObserverSubscription.unsubscribe();
      this.mapObserverSubscription = null;
    }
  }
}